import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import { t } from 'i18next';
import FieldFileInput from '../ReactComponent/FileUploadButton';
import { phoneNumber, digitOnly, currencyOnly } from '../../utils/mask';
import Edit from '../../assets/icons/Edit.svg';
import Plus from '../../assets/icons/Plus.svg';
import Image from '../../assets/icons/Image.svg';
import {
  isRequired, maxLength, isPhone, isEmail, maxChar25,
} from '../../utils/validator';
import {
  Icon,
  ContentBoxLightGrey,
  SummaryText,
  EditButton,
  FileName,
  FileUploadLabel,
  FileUploadContainer,
  FileUpload,
  MainRow,
  DivContainer,
  DivRender,
  Checkboxes,
  CategoryTitle,
  DeleteItemButton,
} from './css';
import {
  H2, H3, P, PLight,
} from '../../theme/typography';
import RenderField from '../ReactComponent/RenderField';
import UploadImages from '../ReactComponent/UploadImages';
import Row from '../ReactComponent/Row';
import Column from '../ReactComponent/Column';
import { formatMoney } from '../../utils/func';
import TextInput from '../../lib/ui/form_elements/TextInput';
import DatePicker from '../../lib/ui/form_elements/DatePicker';
import Dropdown from '../../lib/ui/form_elements/Dropdown';

const maxChar6 = maxLength(6);
const maxChar50 = maxLength(50);
const maxChar255 = maxLength(255);

let inCaseDamage;
const handleCheckboxOnChange = () => {
  const checkedValue = document.querySelector('#claimTypeOption input:checked').nextElementSibling.innerText.toLocaleLowerCase();

  if (checkedValue === 'damage' || checkedValue === 'Dommage'.toLowerCase()) {
    inCaseDamage = true;
  } else {
    inCaseDamage = false;
  }

  return inCaseDamage;
};

const renderFreightRepeatable = ({ fields }) => (
  <>
    {fields.map((part, index) => (
      <div key={index}>
        <Row>
          <Column $tablet={8} $mobile={8}>
            <Field
              name={`${part}.PartDescription`}
              component={TextInput}
              label={t('PartDescription')}
              required
              validate={[maxChar255, isRequired]}
            />
          </Column>
          <Column $tablet={4} $mobile={4}>
            <Field
              name={`${part}.ClaimAmount`}
              component={TextInput}
              label={t('ClaimAmount')}
              required
              validate={[maxChar50, isRequired]}
              normalize={currencyOnly}
            />
          </Column>
          <Column $tablet={12} $mobile={12} style={{ marginTop: '16px' }}>
            <UploadImages numberOfPlaceholders={4} name={`${part}.PartFileUpload`} fieldCounter={index} />
          </Column>
          {
              index > 0
              && (
              <Column className="d-flex justify-content-end">
                <DeleteItemButton onClick={() => {
                  fields.remove(index);
                }}
                >
                  {t('Delete')}
                </DeleteItemButton>
              </Column>
              )
            }
        </Row>
      </div>
    ),
    )}
    <FileUploadContainer className="with-dot">
      <FileUpload onClick={() => fields.push({})}>
        <img src={Plus} alt="add more part file" />
        <FileUploadLabel>{t('FileUploadClaimLabel')}</FileUploadLabel>
      </FileUpload>
    </FileUploadContainer>
  </>
);

const renderRepeatable = ({ fields }) => (
  <>
    {fields.map((part, index) => (
      <div key={index}>
        <Row>
          <Column $tablet={8} $mobile={8}>
            <Field
              name={`${part}.PartDescription`}
              component={TextInput}
              label={t('PartDescription')}
              required
              validate={[maxChar50, isRequired]}
            />
          </Column>
          <Column $tablet={4} $mobile={4}>
            <Field
              name={`${part}.PartNumber`}
              component={TextInput}
              label={t('PartNumber')}
              required
              validate={[maxChar50, isRequired]}
            />
          </Column>
          <Column $tablet={12} $mobile={12}>
            <UploadImages numberOfPlaceholders={4} name={`${part}.PartFileUpload`} fieldCounter={index} />
          </Column>
          <Column $tablet={12} $mobile={12}>
            <Field
              name={`${part}.PartDeliveryDate`}
              component={DatePicker}
              label={t('DateOfDelivery')}
              required
              validate={[isRequired]}
              asteriskStyle={{ top: '10px' }}
            />
          </Column>
        </Row>
      </div>
    ),
    )}
    <FileUploadContainer className="with-dot">
      <FileUpload onClick={() => fields.push({})}>
        <img src={Plus} alt="add more part file" />
        <FileUploadLabel>{t('FileUploadLabel')}</FileUploadLabel>
      </FileUpload>
    </FileUploadContainer>
  </>
);

// eslint-disable-next-line no-unused-vars
export const Step1 = ({ step, fields, isFreight }) => {
  if (step === 0) {
    return (
      <MainRow>
        <Column>
          <Row style={{ marginBottom: '2rem' }}>
            <Column $tablet={12} $mobile={12}>
              <Field
                name="FreightBill"
                type="text"
                component={TextInput}
                label={t('ColumnFreightBill')}
                required
                validate={[isRequired, maxChar25]}
              />
            </Column>
          </Row>
          {!isFreight ? (
            <>
              <Row style={{ marginBottom: '2rem' }}>
                <Column $tablet={12} $mobile={12}>
                  <Field
                    name="DealerName"
                    type="text"
                    component={TextInput}
                    required
                    label={t('DealerName')}
                    validate={[isRequired, maxChar50]}
                  />
                </Column>
              </Row>
              <Row style={{ marginBottom: '2rem' }}>
                <Column $tablet={12} $mobile={12}>
                  <Field
                    name="DealerCode"
                    type="text"
                    component={TextInput}
                    label={t('DealerCode')}
                    required
                    validate={[isRequired, maxChar50]}
                  />
                </Column>
              </Row>
              <Row style={{ marginBottom: '2rem' }}>
                <Column $tablet={12} $mobile={12}>
                  <Field
                    name="DealerCompleteAddress"
                    type="address"
                    component={TextInput}
                    label={t('Address')}
                    validate={[maxChar50, isRequired]}
                    required
                  />
                </Column>
              </Row>
              <Row style={{ marginBottom: '2rem' }}>
                <Column $tablet={12} $mobile={12}>
                  <Field
                    name="CompanyName"
                    type="text"
                    component={TextInput}
                    label={t('CompanyName')}
                    required
                    validate={[isRequired, maxChar50]}
                  />
                </Column>
              </Row>
              <Row style={{ marginBottom: '2rem' }}>
                <Column $tablet={12} $mobile={12}>
                  <Field
                    name="ContactName"
                    type="text"
                    component={TextInput}
                    label={t('ContactName')}
                    required
                    validate={[isRequired, maxChar50]}
                  />
                </Column>
              </Row>
            </>
          )
            : (
              <>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ReferenceNumber"
                      type="text"
                      component={TextInput}
                      label={t('CustomerReferenceNumber')}
                      required
                      validate={[isRequired, maxChar50]}
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ClaimantName"
                      type="text"
                      component={TextInput}
                      label={t('CompanyName')}
                      required
                      validate={[isRequired, maxChar50]}
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ClaimantAddress"
                      type="address"
                      component={TextInput}
                      label={t('Address')}
                      validate={[maxChar50, isRequired]}
                      required
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ContactName"
                      type="text"
                      component={TextInput}
                      label={t('ContactName')}
                      required
                      validate={[isRequired, maxChar50]}
                    />
                  </Column>
                </Row>
              </>
            )}
          <Row style={{ marginBottom: '2rem' }}>
            <Column $tablet={8} $mobile={8}>
              <Field
                name="PhoneNumber"
                component={TextInput}
                label={t('PhoneNumber')}
                required
                validate={[isRequired, isPhone, maxChar50]}
                normalize={phoneNumber}
              />
            </Column>
            <Column $tablet={4} $mobile={4}>
              <Field
                name="Extension"
                component={TextInput}
                label={t('Extension')}
                validate={[maxChar6]}
                normalize={digitOnly}
              />
            </Column>
          </Row>
          <Row style={{ marginBottom: '2rem' }}>
            <Column $tablet={12} $mobile={12}>
              <Field
                name="EmailAddress"
                type="email"
                component={TextInput}
                label={t('EmailAddress')}
                required
                validate={[isRequired, isEmail, maxChar50]}
              />
            </Column>
          </Row>

        </Column>
      </MainRow>
    );
  }

  return null;
};

Step1.propTypes = {
  step: PropTypes.number,
  fields: PropTypes.object,
  isFreight: PropTypes.bool,
};

export const Step2 = ({
  // eslint-disable-next-line no-unused-vars
  formData = { PartsInformation: [{ ClaimAmount: '0' }] }, step, fields, isFreight, updateTotalClaimValue,
}) => {
  if (step === 1) {
    let result = 0;
    formData.PartsInformation.forEach((each) => {
      if (each.ClaimAmount) {
        result += Number(each.ClaimAmount);
      }
    });
    updateTotalClaimValue(result);
    return (
      <MainRow>
        <Column>
          {!isFreight // dedicated
            ? (
              <>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <H3>{t('TypeOfClaim')}</H3>
                    <Field
                      name="TypeOfClaim"
                      type="checkboxes"
                      component={RenderField}
                      data={[t('Damage'), t('Shortage')]}
                      required
                      validate={[isRequired]}
                      checkboxWidth="130px"
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <H2>{t('ClaimValue')}</H2>
                    <Row>
                      <Column $tablet={8} $mobile={8}>
                        <Field
                          name="ClaimValue"
                          component={TextInput}
                          label={t('ClaimAmount')}
                          required
                          validate={[isRequired, maxChar50]}
                          normalize={currencyOnly}
                        />
                      </Column>
                      <Column $tablet={4} $mobile={4}>
                        <Field
                          name="Currency"
                          type="dropdown"
                          component={Dropdown}
                          options={[{ value: 'USD' }, { value: 'CAD' }]}
                          label={t('Currency')}
                          validate={[isRequired]}
                          required
                        />
                      </Column>
                    </Row>
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <H3>{t('ContactHeading')}</H3>
                    <Field
                      name="ContactedCustomerService"
                      type="checkboxes"
                      component={RenderField}
                      data={[t('Yes'), t('No')]}
                      validate={[isRequired]}
                      required
                      checkboxWidth="100px"
                    />
                  </Column>
                </Row>
                {
                  formData.ContactedCustomerService === 'Yes' && (
                    <Row style={{ marginBottom: '2rem' }}>
                      <Column $tablet={12} $mobile={12}>
                        <Field
                          name="DateContactCustomerService"
                          type="date"
                          component={TextInput}
                          label={t('DateContacted')}
                          validate={[isRequired]}
                          required
                        />
                      </Column>
                    </Row>
                  )
                }
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="DescriptionOfTheClaim"
                      type="textarea"
                      component={TextInput}
                      label={t('ClaimDescription')}
                      validate={[maxChar255]}
                    />
                  </Column>
                </Row>
                <H2 className="pad-t-5 mar-t-5">{t('PartsInformation')}</H2>
                <FieldArray name="PartsInformation" component={renderRepeatable} />
              </>
            ) // Freight/same-day
            : (
              <>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ShipperName"
                      type="text"
                      component={TextInput}
                      label={t('ShipperName')}
                      validate={[maxChar50, isRequired]}
                      required
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={8} $mobile={8}>
                    <Field
                      name="ShipperTelephoneNumber"
                      component={TextInput}
                      label={t('ShipperPhoneNumber')}
                      validate={[isRequired, isPhone, maxChar50]}
                      required
                      normalize={phoneNumber}
                    />
                  </Column>
                  <Column $tablet={4} $mobile={4}>
                    <Field
                      name="ShipperExtension"
                      component={TextInput}
                      label={t('Extension')}
                      validate={[maxChar6]}
                      normalize={digitOnly}
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12}>
                    <Field
                      name="ConsigneeName"
                      type="text"
                      component={TextInput}
                      label={t('ConsigneeName')}
                      validate={[maxChar50, isRequired]}
                      required
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={8} $mobile={8}>
                    <Field
                      name="ConsigneeTelephoneNumber"
                      component={TextInput}
                      label={t('ConsigneePhoneNumber')}
                      required
                      validate={[isRequired, isPhone, maxChar50]}
                      normalize={phoneNumber}
                    />
                  </Column>
                  <Column $tablet={4} $mobile={4}>
                    <Field
                      name="ConsigneeExtension"
                      component={TextInput}
                      label={t('Extension')}
                      validate={[maxChar6]}
                      normalize={digitOnly}
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column $tablet={12} $mobile={12} id="claimTypeOption" onChange={() => handleCheckboxOnChange()}>
                    <CategoryTitle>{t('ClaimIsFor')}</CategoryTitle>
                    <Checkboxes
                      name="TypeOfClaim"
                      type="checkboxes"
                      component={RenderField}
                      required
                      data={[t('Damage'), t('Shortage'), t('Loss'), t('Incident'), t('Overage'), t('Refused')]}
                      validate={[isRequired]}
                      checkboxWidth="130px"
                    />

                  </Column>
                </Row>
                {inCaseDamage ? (
                  <Row style={{ marginBottom: '2rem' }}>
                    <Column>
                      <DivRender>
                        <Field
                          name="AdditionalDamageInfo"
                          type="checkboxes"
                          component={RenderField}
                          data={[t('Yes'), t('No')]}
                          required
                          label={t('AdditionalDamageInfo')}
                          validate={[isRequired]}
                          checkboxWidth="100px"
                        />
                      </DivRender>

                    </Column>
                  </Row>
                )
                  : (
                    <>
                    </>
                  )}
                <Row style={{ marginBottom: '2rem' }}>
                  <Column>
                    <Field
                      name="DamageExplanation"
                      type="textarea"
                      component={TextInput}
                      label={t('DamageExplanation')}
                      validate={[maxChar255, isRequired]}
                      required
                    />
                  </Column>
                </Row>
                <Row style={{ marginBottom: '2rem' }}>
                  <Column>
                    <Field
                      name="QualityControl"
                      type="checkboxes"
                      component={RenderField}
                      checkboxWidth="100px"
                      data={[t('Yes'), t('No')]}
                      validate={[isRequired]}
                      label={t('QualityControl')}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <DivContainer>
                      <H3>{t('DetailedDescriptionOfClaim')}</H3>
                    </DivContainer>
                    <PLight style={{ marginBottom: '32px' }}>{t('DetailedDescriptionOfClaimParagraph')}</PLight>
                  </Column>
                </Row>

                <H2>{t('PartsInformation')}</H2>
                <FieldArray name="PartsInformation" component={renderFreightRepeatable} />
                <Row className="justify-content-end" style={{ marginBottom: '2rem' }}>
                  <Column $tablet={3} $mobile={6}>
                    <Field
                      name="Currency"
                      type="dropdown"
                      component={Dropdown}
                      options={[{ value: 'USD' }, { value: 'CAD' }]}
                      label={t('Currency')}
                      validate={[isRequired]}
                      required
                    />
                  </Column>
                  <Column $tablet={4} $mobile={6}>
                    <Field
                      name="TotalClaimValue"
                      component={TextInput}
                      label={t('TotalClaimAmount')}
                      normalize={currencyOnly}
                      meta={{ active: true, touched: true }}
                      disabled
                      input={{ value: result.toString() }}
                    />
                  </Column>
                </Row>
              </>
            )}
        </Column>
      </MainRow>
    );
  }

  return null;
};

Step2.propTypes = {
  formData: PropTypes.shape({
    PartsInformation: PropTypes.arrayOf(
      PropTypes.shape({
        ClaimAmount: PropTypes.string,
      }),
    ),
    ContactedCustomerService: PropTypes.string,
  }),
  step: PropTypes.number,
  fields: PropTypes.object,
  isFreight: PropTypes.bool,
  updateTotalClaimValue: PropTypes.func,
};

// eslint-disable-next-line no-unused-vars
export const Step3 = ({ formData = {}, step, fields }) => {
  const { PartsInformation } = formData;

  if (step === 2) {
    return (
      <MainRow>
        <Column>
          {PartsInformation && (
          <ContentBoxLightGrey>
            <H2 className="center-text-mobile">{t('PhotosUploaded')}</H2>
            {PartsInformation.map((each, key) => (
              <React.Fragment key={key}>
                <PLight>{t('PartDescription')}</PLight>
                <SummaryText className="no-margin-bottom">{each.PartDescription}</SummaryText>
                {each.PartFileUpload
                      && (Object.keys(each.PartFileUpload).map((item) => (
                        <FileName key={item}>
                          <img src={Image} alt={each.PartFileUpload[item].name} />
                          {each.PartFileUpload[item].name}
                        </FileName>
                      ))
                      )}
              </React.Fragment>
            ))}
          </ContentBoxLightGrey>
          )}

          <ContentBoxLightGrey>
            <div className="title-box">
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('BillOfLading')}
                name="ProBillNumber"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('FreightBill')}
                name="PaidFreightBill"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('ProofOfDelivery')}
                name="ProofOfDelivery"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('CopyOfClaimantsCostInvoice')}
                name="CopyOfClaimantsCostInvoice"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('CopyOfRepairBill')}
                name="CopyOfRepairBill"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('SupportingDocumentation')}
                name="SupportingDocumentation"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('PicturesOfDamage')}
                name="PicturesOfAnyDamage"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('PicturesOfTrailerOrContainer')}
                name="PicturesOfTrailerOrContainer"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('ClaimForm')}
                name="ClaimForm"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('PackingSlip')}
                name="PackingSlip"
              />
              <Field
                component={FieldFileInput}
                attachButton={t('AttachDocument')}
                label={t('PartsInvoice')}
                name="PartsInvoice"
              />
            </div>
          </ContentBoxLightGrey>
        </Column>
      </MainRow>
    );
  }

  return null;
};

Step3.propTypes = {
  formData: PropTypes.shape({
    PartsInformation: PropTypes.arrayOf(
      PropTypes.shape({
        PartDescription: PropTypes.string,
        PartFileUpload: PropTypes.objectOf(
          PropTypes.shape({
            name: PropTypes.string,
          }),
        ),
      }),
    ),
  }),
  step: PropTypes.number,
  fields: PropTypes.object,
};

export const Step4 = ({
  // eslint-disable-next-line no-unused-vars
  formData = {}, step, editStepOne, editStepTwo, editStepThree, fields, isFreight, totalClaimValue,
}) => {
  const {
    DealerName, DealerCode, DealerCompleteAddress, CompanyName, ContactName, PhoneNumber, EmailAddress, BillOfPayment,
    TypeOfClaim, ClaimValue, ContactedCustomerService, DateContactCustomerService, DescriptionOfTheClaim, PartsInformation,
    ProBillNumber, PaidFreightBill, ProofOfDelivery, CopyOfClaimantsCostInvoice, CopyOfRepairBill, SupportingDocumentation,
    PicturesOfAnyDamage, PicturesOfTrailerOrContainer, ClaimForm, PartsInvoice, PackingSlip, ReferenceNumber, ClaimantName,
    ClaimantAddress, Extension, ShipperName, ShipperTelephoneNumber, ShipperExtension, ConsigneeName, ConsigneeTelephoneNumber,
    ConsigneeExtension, DamageExplanation, Currency, QualityControl, AdditionalDamageInfo,
  } = formData;

  if (step === 3) {
    return (
      <>
        <ContentBoxLightGrey>
          <H2 className="center-text-mobile">
            {t('YourInformation')}
          </H2>
          <EditButton onClick={editStepOne}>
            {t('Edit')}
            {' '}
            <Icon className="icon" src={Edit} alt="edit" />
          </EditButton>
          {!isFreight
            ? (
              <>
                <PLight>{t('DealerName')}</PLight>
                <SummaryText>{DealerName}</SummaryText>
                <PLight>{t('DealerCode')}</PLight>
                <SummaryText>{DealerCode}</SummaryText>
                <PLight>{t('DealerCompleteAddress')}</PLight>
                <SummaryText>{DealerCompleteAddress}</SummaryText>
                <PLight>{t('CompanyName')}</PLight>
                <SummaryText>{CompanyName}</SummaryText>
                <PLight>{t('ContactName')}</PLight>
                <SummaryText>{ContactName}</SummaryText>
              </>
            )
            : (
              <>
                <PLight>{t('CustomerReferenceNumber')}</PLight>
                <SummaryText>{ReferenceNumber}</SummaryText>
                <PLight>{t('ClaimantName')}</PLight>
                <SummaryText>{ClaimantName}</SummaryText>
                <PLight>{t('ClaimantAddress')}</PLight>
                <SummaryText>{ClaimantAddress}</SummaryText>
                <PLight>{t('ContactName')}</PLight>
                <SummaryText>{ContactName}</SummaryText>
              </>
            )}
          <PLight>{t('PhoneNumber')}</PLight>
          <SummaryText>{PhoneNumber}</SummaryText>
          {Extension && (
          <>
            <PLight>{t('Extension')}</PLight>
            <SummaryText>{Extension}</SummaryText>
          </>
          )}
          <PLight>{t('EmailAddress')}</PLight>
          <SummaryText>{EmailAddress}</SummaryText>
        </ContentBoxLightGrey>
        <ContentBoxLightGrey>
          <H2 className="center-text-mobile">
            {t('ClaimDetails')}
          </H2>
          <EditButton onClick={editStepTwo}>
            {t('Edit')}
            {' '}
            <Icon className="icon" src={Edit} alt="edit" />
          </EditButton>
          <PLight>{t('BillOfPayment')}</PLight>
          <SummaryText>{BillOfPayment}</SummaryText>
          {
            !isFreight
              ? (
                <>
                  <PLight>{t('TypeOfClaim')}</PLight>
                  <SummaryText>{TypeOfClaim}</SummaryText>
                  <H2>{t('ClaimValue')}</H2>
                  <PLight>{t('ClaimAmountValue')}</PLight>
                  <SummaryText>{ClaimValue}</SummaryText>
                  <H3>{t('ContactHeading')}</H3>
                  <SummaryText>{ContactedCustomerService}</SummaryText>
                  {
                  ContactedCustomerService === 'Yes' && (
                    <>
                      <PLight>{t('DateContacted')}</PLight>
                      <SummaryText>{DateContactCustomerService}</SummaryText>
                    </>
                  )
                }
                  <PLight>{t('ClaimDescription')}</PLight>
                  <SummaryText>{DescriptionOfTheClaim}</SummaryText>
                  <H2>{t('PartsInformation')}</H2>
                  {PartsInformation && PartsInformation.map((each, key) => (
                    <React.Fragment key={key}>
                      <PLight>{t('PartDescription')}</PLight>
                      <SummaryText className="no-margin-bottom">{each.PartDescription}</SummaryText>
                      {each.PartFileUpload
                  && (Object.keys(each.PartFileUpload).map((item) => (
                    <FileName key={item}>
                      <img src={Image} alt={each.PartFileUpload[item].name} />
                      {' '}
                      {each.PartFileUpload[item].name}
                    </FileName>
                  ))
                  )}
                    </React.Fragment>
                  ))}
                </>
              )
              : (
                <>
                  <PLight>{t('ShipperName')}</PLight>
                  <SummaryText>{ShipperName}</SummaryText>
                  <PLight>{t('ShipperTelephoneNumber')}</PLight>
                  <SummaryText>{ShipperTelephoneNumber}</SummaryText>
                  {
                  ShipperExtension && (
                  <>
                    <PLight>{t('ShipperExtension')}</PLight>
                    <SummaryText>{ShipperExtension}</SummaryText>
                  </>
                  )
                }
                  <PLight>{t('ConsigneeName')}</PLight>
                  <SummaryText>{ConsigneeName}</SummaryText>
                  <PLight>{t('ConsigneeTelephoneNumber')}</PLight>
                  <SummaryText>{ConsigneeTelephoneNumber}</SummaryText>
                  {
                  ConsigneeExtension && (
                  <>
                    <PLight>{t('ConsigneeExtension')}</PLight>
                    <SummaryText>{ConsigneeExtension}</SummaryText>
                  </>
                  )
                }
                  <PLight>{t('ClaimIsFor')}</PLight>
                  <Field
                    name="TypeOfClaimStep4"
                    type="checkboxes"
                    component={RenderField}
                    disabled
                    data={[t('Damage'), t('Shortage'), t('Loss'), t('Delay'), t('Overage'), t('Refused')]}
                    input={{ value: TypeOfClaim }}
                    checkboxWidth="100px"
                  />
                  <PLight>{t('AdditionalDamageInfo')}</PLight>
                  <Field
                    name="AdditionalDamageInfoStep4"
                    type="checkboxes"
                    component={RenderField}
                    data={[t('Yes'), t('No')]}
                    disabled
                    input={{ value: AdditionalDamageInfo }}
                    checkboxWidth="100px"
                  />
                  <PLight>{t('DamageExplanation')}</PLight>
                  <SummaryText>{DamageExplanation}</SummaryText>
                  <PLight>{t('QualityControl')}</PLight>
                  <Field
                    name="QualityControlStep4"
                    type="checkboxes"
                    component={RenderField}
                    data={[t('Yes'), t('No')]}
                    disabled
                    input={{ value: QualityControl }}
                    checkboxWidth="100px"
                  />
                  <H3 style={{ marginBottom: '32px' }}>{t('DetailedDescriptionOfClaim')}</H3>
                  {PartsInformation && PartsInformation.map((each, key) => (
                    <React.Fragment key={key}>
                      <PLight>{t('PartDescription')}</PLight>
                      <SummaryText className="">{each.PartDescription}</SummaryText>
                      <PLight>{t('ClaimAmount')}</PLight>
                      <SummaryText className="no-margin-bottom">{formatMoney(each.ClaimAmount)}</SummaryText>
                      {each.PartFileUpload
                  && (Object.keys(each.PartFileUpload).map((item) => (
                    <FileName key={item}>
                      <img src={Image} alt={each.PartFileUpload[item].name} />
                      {' '}
                      {each.PartFileUpload[item].name}
                    </FileName>
                  ))
                  )}
                    </React.Fragment>
                  ))}
                  <PLight>{t('Currency')}</PLight>
                  <SummaryText className="">{Currency}</SummaryText>
                  <PLight>{t('TotalClaimAmount')}</PLight>
                  <SummaryText className="">{formatMoney(totalClaimValue)}</SummaryText>

                </>
              )
          }
        </ContentBoxLightGrey>
        <ContentBoxLightGrey>
          <H2 className="center-text-mobile">
            {t('DocumentsAttached')}
          </H2>
          <EditButton onClick={editStepThree}>
            {t('Edit')}
            <Icon className="icon" src={Edit} alt="edit" />
          </EditButton>
          {ProBillNumber && (
            <FileUploadContainer className="with-background">
              <P>{t('BillOfLading')}</P>
              <FileName className="inline">
                <img src={Image} alt="ProBillNumber" />
                {' '}
                {ProBillNumber.name}
              </FileName>
            </FileUploadContainer>
          )}
          {PaidFreightBill && (
            <FileUploadContainer className="with-background">
              <P>{t('CopyOfRepairBill')}</P>
              <FileName className="inline">
                <img src={Image} alt="PaidFreightBill" />
                {' '}
                {PaidFreightBill.name}
              </FileName>
            </FileUploadContainer>
          )}
          {ProofOfDelivery && (
            <FileUploadContainer className="with-background">
              <P>{t('ProofOfDelivery')}</P>
              <FileName className="inline">
                <img src={Image} alt="ProofOfDelivery" />
                {' '}
                {ProofOfDelivery.name}
              </FileName>
            </FileUploadContainer>
          )}
          {CopyOfClaimantsCostInvoice && (
            <FileUploadContainer className="with-background">
              <P>{t('CopyOfClaimantsCostInvoice')}</P>
              <FileName className="inline">
                <img src={Image} alt="CopyOfClaimantsCostInvoice" />
                {' '}
                {CopyOfClaimantsCostInvoice.name}
              </FileName>
            </FileUploadContainer>
          )}
          {CopyOfRepairBill && (
            <FileUploadContainer className="with-background">
              <P>{t('CopyOfRepairBill')}</P>
              <FileName className="inline">
                <img src={Image} alt="CopyOfRepairBill" />
                {' '}
                {CopyOfRepairBill.name}
              </FileName>
            </FileUploadContainer>
          )}
          {SupportingDocumentation && (
            <FileUploadContainer className="with-background">
              <P>{t('SupportingDocumentation')}</P>
              <FileName className="inline">
                <img src={Image} alt="SupportingDocumentation" />
                {' '}
                {SupportingDocumentation.name}
              </FileName>
            </FileUploadContainer>
          )}
          {PicturesOfAnyDamage && (
            <FileUploadContainer className="with-background">
              <P>{t('PicturesOfDamage')}</P>
              <FileName className="inline">
                <img src={Image} alt="PicturesOfAnyDamage" />
                {' '}
                {PicturesOfAnyDamage.name}
              </FileName>
            </FileUploadContainer>
          )}
          {PicturesOfTrailerOrContainer && (
            <FileUploadContainer className="with-background">
              <P>{t('PicturesOfTrailerOrContainer')}</P>
              <FileName className="inline">
                <img src={Image} alt="PicturesOfTrailerOrContainer" />
                {' '}
                {PicturesOfTrailerOrContainer.name}
              </FileName>
            </FileUploadContainer>
          )}
          {ClaimForm && (
            <FileUploadContainer className="with-background">
              <P>{t('ClaimForm')}</P>
              <FileName className="inline">
                <img src={Image} alt="ClaimForm" />
                {' '}
                {ClaimForm.name}
              </FileName>
            </FileUploadContainer>
          )}
          {PackingSlip && (
            <FileUploadContainer className="with-background">
              <P>{t('PackingSlip')}</P>
              <FileName className="inline">
                <img src={Image} alt="PackingSlip" />
                {' '}
                {PackingSlip.name}
              </FileName>
            </FileUploadContainer>
          )}
          {PartsInvoice && (
            <FileUploadContainer className="with-background">
              <P>{t('PartsInvoice')}</P>
              <FileName className="inline">
                <img src={Image} alt="PartsInvoice" />
                {' '}
                {PartsInvoice.name}
              </FileName>
            </FileUploadContainer>
          )}
        </ContentBoxLightGrey>
      </>
    );
  }

  return null;
};

Step4.propTypes = {
  formData: PropTypes.shape({
    DealerName: PropTypes.string,
    DealerCode: PropTypes.string,
    DealerCompleteAddress: PropTypes.string,
    CompanyName: PropTypes.string,
    ContactName: PropTypes.string,
    PhoneNumber: PropTypes.string,
    EmailAddress: PropTypes.string,
    BillOfPayment: PropTypes.string,
    TypeOfClaim: PropTypes.string,
    ClaimValue: PropTypes.string,
    ContactedCustomerService: PropTypes.string,
    DateContactCustomerService: PropTypes.string,
    DescriptionOfTheClaim: PropTypes.string,
    PartsInformation: PropTypes.arrayOf(
      PropTypes.shape({
        PartDescription: PropTypes.string,
        PartFileUpload: PropTypes.objectOf(
          PropTypes.shape({
            name: PropTypes.string,
          }),
        ),
      }),
    ),
    ProBillNumber: PropTypes.shape({
      name: PropTypes.string,
    }),
    PaidFreightBill: PropTypes.shape({
      name: PropTypes.string,
    }),
    ProofOfDelivery: PropTypes.shape({
      name: PropTypes.string,
    }),
    CopyOfClaimantsCostInvoice: PropTypes.shape({
      name: PropTypes.string,
    }),
    CopyOfRepairBill: PropTypes.shape({
      name: PropTypes.string,
    }),
    SupportingDocumentation: PropTypes.shape({
      name: PropTypes.string,
    }),
    PicturesOfAnyDamage: PropTypes.shape({
      name: PropTypes.string,
    }),
    PicturesOfTrailerOrContainer: PropTypes.shape({
      name: PropTypes.string,
    }),
    ClaimForm: PropTypes.shape({
      name: PropTypes.string,
    }),
    PackingSlip: PropTypes.shape({
      name: PropTypes.string,
    }),
    PartsInvoice: PropTypes.shape({
      name: PropTypes.string,
    }),
    ReferenceNumber: PropTypes.string,
    ClaimantName: PropTypes.string,
    ClaimantAddress: PropTypes.string,
    Extension: PropTypes.string,
    ShipperName: PropTypes.string,
    ShipperTelephoneNumber: PropTypes.string,
    ShipperExtension: PropTypes.string,
    ConsigneeName: PropTypes.string,
    ConsigneeTelephoneNumber: PropTypes.string,
    ConsigneeExtension: PropTypes.string,
    DamageExplanation: PropTypes.string,
    Currency: PropTypes.string,
    QualityControl: PropTypes.string,
    AdditionalDamageInfo: PropTypes.string,
  }),
  step: PropTypes.number,
  editStepOne: PropTypes.func,
  editStepTwo: PropTypes.func,
  editStepThree: PropTypes.func,
  fields: PropTypes.object,
  isFreight: PropTypes.bool,
  totalClaimValue: PropTypes.number,
};
